import * as React from "react"
import { Col, Hidden, Row, Visible } from "react-grid-system"
import SearchField from "../../atoms/inputs/SearchField"
import Dropdown, { optionsMap } from "../../atoms/inputs/Dropdown"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import OrganizationMembersCard from "../../organisms/OrganizationMembersCard"
import { AnimatePresence } from "framer-motion"
import Modal from "../../molecules/Modals/Modal"
import IconButton from "../../atoms/buttons/IconButton"
import InviteToGroupModalContent from "../../molecules/Modals/InviteToGroupModalContent"
import ListIcon from "../../atoms/icons/ListIcon"
import DetailsIcon from "../../atoms/icons/DetailsIcon"
import { theme } from "../../theme"
import { formatAllOrganizationMembers } from "../../../utils/formatters"
import { useTranslation } from "react-i18next"
import { useMyOrganization } from "../../../hooks/useMyOrganization"
import { useInviteMembersToGroup } from "../../../hooks/useinviteMembersToGroup"
import FilterDropdown from "../../atoms/inputs/FilterDropdown"
import { useOrganizations } from "../../../hooks/useOrganizations"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import { Member } from "../../../utils/models"
import { useNavigate, useParams } from "react-router-dom"
import { PAGINATION_VALUE, PAGINATION_VALUE_PRESETS } from "../../../utils/design-helpers"
import { useAllMembers } from "../../../hooks/useAllMembers"
import PaginationComponent from "../../molecules/PaginationComponent"
import { useMyOrganizationPendingInvitations } from "../../../hooks/useMyOrganizationPendingInvitations"
import H4 from "../../atoms/typography/H4"
import Body from "../../atoms/typography/Body"
import { ApolloError } from "@apollo/client"
import { captureExceptionWithContext } from "../../../utils/sentry"
import { Key } from "react-aria-components"
import Button from "../../atoms/buttons/Button"

const DROPDOWN_OPTIONS = [
  { id: "name", value: "name", name: "Name A-Z" },
  { id: "-name", value: "-name", name: "Name Z-A" },
  { id: "title", value: "title", name: "Title A-Z" },
  { id: "-title", value: "-title", name: "Title Z-A" },
  { id: "organization", value: "organization", name: "Suborganization A-Z" },
  { id: "-organization", value: "-organization", name: "Suborganization Z-A" },
  { id: "role", value: "role", name: "Organization role Z-A" },
  { id: "-role", value: "-role", name: "Organization role Z-A" },
]
const MembersPage: React.FunctionComponent = () => {
  const { t } = useTranslation()
  const { page } = useParams()
  const navigate = useNavigate()

  const { myOrganization } = useMyOrganization()
  const { organizations } = useOrganizations()
  const { pendingInvites } = useMyOrganizationPendingInvitations()

  const [currentPage, setCurrentPage] = React.useState<number>(parseInt(page ?? "1"))
  const [offset, setoffset] = React.useState<number>(currentPage > 1 ? (currentPage - 1) * PAGINATION_VALUE : 0)

  const [searchValue, setSearchValue] = React.useState<undefined | string>(undefined)
  const [sortingId, setSortingId] = React.useState<Key>("name")

  const sorting = optionsMap(DROPDOWN_OPTIONS)[sortingId].value!

  const [inviteToGroupModalOpen, setInviteToGroupModalOpen] = React.useState(false)

  const onSearch = (value: any) => {
    setSearchValue(value)
  }

  const organizationsToUse: Array<{ id: number | string; title: string }> = []
  organizations?.map((org: any) => organizationsToUse.push({ id: org.id, title: org.name }))
  const [selectedFilterIds, setSelectedFilterIds] = React.useState<Array<string | number>>([])
  const onClickFilterItem = (id: string | number) => {
    if (selectedFilterIds.includes(id)) {
      const filterd = selectedFilterIds.filter((x) => x !== id)
      setSelectedFilterIds(filterd)
    } else {
      setSelectedFilterIds([...selectedFilterIds, id])
    }
  }
  const onClickSelectAllFilterOptions = () => {
    const organizationIds = organizationsToUse.map((org) => org.id)
    setSelectedFilterIds(organizationIds)
  }

  const { allMembers, isLoading } = useAllMembers(PAGINATION_VALUE, offset, searchValue, selectedFilterIds, sorting)

  React.useEffect(() => {
    if (myOrganization) {
      setSelectedFilterIds([myOrganization.id])
    }
  }, [myOrganization])

  const formattedMembers = formatAllOrganizationMembers(allMembers?.edges ?? [])

  const [selectedMember, setSelectedMember] = React.useState<undefined | Member>(undefined)
  const onClickOpenInviteMemberToGroupModal = (member: Member) => {
    setSelectedMember(member)
    setInviteToGroupModalOpen(true)
  }
  const onClickInviteMemberToGroup = (gid: number | string) => {
    if (selectedMember) {
      onSendInvite(selectedMember.member.email, "Viewer", gid)
    }
  }

  const onCompletedSendInviteToMember = () => {
    console.log("Sent invite to member")
    setSelectedMember(undefined)
    setInviteToGroupModalOpen(false)
  }
  const onErrorSendInviteToMember = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, "error sending invite to member")
  }
  const { inviteMembersToGroup } = useInviteMembersToGroup({
    onCompleted: onCompletedSendInviteToMember,
    onError: onErrorSendInviteToMember,
  })
  const onSendInvite = async (email: string, role: string, groupId: string | number) => {
    try {
      console.log(`Sending invite to ${email} to join group ${groupId} as ${role}`)
      await inviteMembersToGroup({
        variables: {
          organizationGroupId: groupId,
          organizationId: selectedMember?.member.organizationId ?? "error",
          users: [{ email: email, role: role }],
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error sending invite to member")
    }
  }

  //Handle open sections
  const [cardsOpen, setCardsOpen] = React.useState<Array<string>>([])
  const checkCardIsOpen = (id: string) => {
    return cardsOpen.includes(id)
  }
  const onClickHandleOpenCard = (id: string) => {
    if (checkCardIsOpen(id)) {
      const arr = cardsOpen.filter((x) => x !== id)
      setCardsOpen(arr)
    } else {
      setCardsOpen([...cardsOpen, id])
    }
  }
  const onClickListView = () => {
    setCardsOpen([])
  }
  const allMemberIds: Array<string> = []
  formattedMembers?.map((member: any) => allMemberIds.push(member.member.id))
  const onClickDetailsView = () => {
    setCardsOpen(allMemberIds)
  }

  //Handle pagination
  const totalCount = allMembers?.totalCount ?? 1
  const pagesInitialValue = Math.ceil(totalCount / PAGINATION_VALUE)
  const totalPages = totalCount < PAGINATION_VALUE ? 1 : pagesInitialValue
  const onClickPage = (newPage: number) => {
    if (newPage !== currentPage) {
      setoffset(offset ? PAGINATION_VALUE * (newPage - 1) : PAGINATION_VALUE)
      setCurrentPage(newPage)
      navigate(`/organization/members/${newPage}`)
    }
  }

  return (
    <div>
      <Row align="end">
        <Col lg={2}>
          <SearchField onChange={(e) => onSearch(e)} />
          <Visible xs md>
            <HorizontalSpacer spacing={2} />
          </Visible>
        </Col>
        <Col lg={2} md={6}>
          <Dropdown
            options={DROPDOWN_OPTIONS}
            label={t("sort_by")}
            onSelectionChange={(id) => setSortingId(id)}
            selectedKey={sortingId}
          />
        </Col>
        <Col lg={2} md={6}>
          <FilterDropdown
            title={t("filter")}
            items={organizationsToUse}
            onClickItem={(e) => onClickFilterItem(e)}
            selectedValues={selectedFilterIds}
            selectAllTitle={t("all_organizations")}
            onClickSelectAll={onClickSelectAllFilterOptions}
          />
        </Col>
        <Hidden xs md>
          <Col md={2}>
            <div style={{ display: "flex" }}>
              <Button
                iconLeft={<ListIcon small color={cardsOpen.length < 1 ? theme.colors.white : theme.colors.greyDark} />}
                onPress={onClickListView}
                variant={cardsOpen.length < 1 ? "dark" : "light"}
              />
              <Spacer spacing={1} />
              <Button
                variant={cardsOpen.length === allMemberIds.length ? "dark" : "light"}
                iconLeft={
                  <DetailsIcon
                    small
                    color={cardsOpen.length === allMemberIds.length ? theme.colors.white : theme.colors.greyDark}
                  />
                }
                onPress={onClickDetailsView}
              />
            </div>
          </Col>
        </Hidden>
      </Row>
      <HorizontalSpacer spacing={2} />
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <OrganizationMembersCard
          members={formattedMembers}
          pendingInvites={pendingInvites ?? []}
          onClickInviteToGroup={onClickOpenInviteMemberToGroupModal}
          cardsOpen={cardsOpen}
          onClickHandleOpenCard={onClickHandleOpenCard}
        />
      )}

      <HorizontalSpacer spacing={4} />
      <Row justify="center">
        <PaginationComponent
          itemsPerPageValues={PAGINATION_VALUE_PRESETS}
          pages={totalPages}
          currentPageIndex={currentPage}
          onClickPage={onClickPage}
        />
      </Row>

      <HorizontalSpacer spacing={3} />
      <H4>Why should you invite members? </H4>
      <HorizontalSpacer spacing={1} />
      <Body>
        Invite the members of your organization to enable them to keep track of terminals and data, as well as to top up
        data when needed. Inviting members to your organization will also allow you to manage terminals and usage
        through groups. Members can be assigned viewer or admin status to ensure that no unauthorized changes or
        purchases are made.
      </Body>
      <HorizontalSpacer spacing={3} />

      <AnimatePresence initial={false} mode="wait" onExitComplete={() => null}>
        {inviteToGroupModalOpen && (
          <Modal
            open={inviteToGroupModalOpen}
            onClose={() => setInviteToGroupModalOpen(!inviteToGroupModalOpen)}
            closeIcon
          >
            <InviteToGroupModalContent
              memberName={selectedMember?.member.name ?? ""}
              userId={selectedMember?.member.id}
              onCloseModal={() => setInviteToGroupModalOpen(!inviteToGroupModalOpen)}
              onClickInviteMemberToGroup={onClickInviteMemberToGroup}
            />
          </Modal>
        )}
      </AnimatePresence>
    </div>
  )
}

export default MembersPage
